import { useEffect, useState } from 'react';
import { message, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import ApiError from '../core/ApiError';
import SurveysService from './SurveysService';
import QuestionsList, { IResponse } from './components/QuestionsList';

import styles from './surveypage.module.css';
import Helmet from 'react-helmet';
import { isAuthenticated } from '../auth/components/RenderForRole';
import { useNavigate, useParams } from 'react-router-dom';

const SurveyPage = () => {
    const navigate = useNavigate();
    const { muuid } = useParams() as { muuid: string };
    const qs = document.location.search;

    if (!/[0-9a-fA-F]{6}[0-9a-fA-F]{6}/.test(muuid))
        navigate('/404', { replace: true });

    const [response, setResponse] = useState<any>(null);
    const [survey, setSurvey] = useState<any>(null);
    const [bgImage, setBgImage] = useState<string>();
    const [bgColor, setBgColor] = useState<string>();

    const saveQuestionResponse = async (
        questionId: number,
        { value, text, options, time, meta }: IResponse
    ) => {
        try {
            const result = await SurveysService.saveQuestionResponse(
                survey.id,
                response.id,
                questionId,
                time ?? 0,
                text,
                value,
                options
            );

            response.question_responses = response.question_responses || [];
            response.question_responses.push(result);

            if (typeof meta === 'object') {
                for (const key in meta) {
                    await SurveysService.saveQuestionResponse(
                        survey.id,
                        response.id,
                        Number(key),
                        0,
                        undefined,
                        meta[key]
                    );
                }
            }

            setResponse(response);
        } catch (err) {
            throw err;
        }
    };

    const onQuestionSubmit = async (
        questionId: number,
        response: IResponse
    ) => {
        try {
            await saveQuestionResponse(questionId, response);
            return true;
        } catch (err) {
            message.error((err as Error).message);
            return false;
        }
    };

    const onSurveyFinished = async () => {
        try {
            await SurveysService.finishResponse(survey.id, response.id);
        } catch (err) {
            throw err;
        }
    };

    useEffect(() => {
        const querySurvey = async (uuid: string, cuuid: string) => {
            try {
                const survey = await SurveysService.getPublicSurveyByUUID(
                    uuid,
                    cuuid
                );
                const response = await SurveysService.saveResponse(
                    survey.id,
                    survey.config ? survey.config.id : null,
                    qs
                );

                const theme = survey
                    ? { ...survey.theme, ...(survey.config || {}).theme }
                    : null;

                let bgImage = undefined;
                let bgColor = undefined;

                if (theme) {
                    if (theme.background_image) {
                        var gradColor =
                            theme.background_color || 'rgba(0,0,0,0)';
                        bgImage = `linear-gradient(0deg, ${gradColor}, ${gradColor}), url(${theme.background_image})`;
                    } else {
                        bgColor = theme.background_color || undefined;
                    }
                }

                setResponse(response);
                setSurvey(survey);
                setBgImage(bgImage);
                setBgColor(bgColor);
            } catch (err) {
                if (err instanceof ApiError && err.data.code === '20001') {
                    navigate('/404', { replace: true });
                } else {
                    message.error((err as Error).message);
                }
            }
        };
        const id = muuid.substring(0, 5);
        const cid = muuid.substring(5);
        if (id === '6328aa' && cid === '07f68a')
            navigate('./bb57fa45d971', { replace: true });

        querySurvey(id, cid);
    }, [muuid, navigate, qs]);

    return (
        <>
            <div
                className={`${styles.bg} qr-bg`}
                style={{ backgroundImage: bgImage, backgroundColor: bgColor }}
            ></div>
            <div className={styles.surveyPage}>
                <QuestionsList
                    mode="FillOut"
                    survey={survey}
                    questions={survey ? survey.questions : null}
                    lazyRender
                    onQuestionSubmit={onQuestionSubmit}
                    onSurveyFinished={onSurveyFinished}
                />
            </div>
            {!!survey && (
                <>
                    <Helmet>
                        <title>{`Intrena QR: ${survey.title} - ${survey.config.title}`}</title>
                    </Helmet>
                    {isAuthenticated() && (
                        <Tooltip
                            title={`${survey.title} - ${survey.config.title}`}
                            className={styles.infoButton}
                            placement="bottomLeft"
                        >
                            <InfoCircleOutlined />
                        </Tooltip>
                    )}
                </>
            )}
        </>
    );
};

export default SurveyPage;
